import { useId, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from "@/components/ui/dialog";
import type { ClientResponse } from "@patent-studio/api-types";
import { useCreateClient } from "@/hooks/use-clients";
import { refusalDetail } from "@/services/api-client";
import {
  type ContactDraft,
  KeyContactSection,
  NO_CONTACT,
  TextField,
  givenContact,
} from "./ClientField";
import { LogoField, usePreview } from "./LogoField";

interface Draft {
  name: string;
  industry: string;
  contact: ContactDraft;
}

const EMPTY: Draft = { name: "", industry: "", contact: NO_CONTACT };

/** The fields a card cannot be drawn without, each filled with more than space. */
export function isComplete(draft: { name: string; industry: string }): boolean {
  return [draft.name, draft.industry].every((value) => value.trim() !== "");
}

/** Adds the client, then its logo, and says what happened. */
function useAddClient(onAdded: (client: ClientResponse) => void) {
  const create = useCreateClient();
  async function add(draft: Draft, logo: File | null) {
    if (!isComplete(draft) || create.isPending) return;
    try {
      const contact = givenContact(draft.contact);
      const { client, logoRefused } = await create.mutateAsync({
        details: {
          name: draft.name.trim(),
          industry: draft.industry.trim(),
          ...(contact === undefined ? {} : { key_contact: contact }),
        },
        logo,
      });
      toast.success("Client added. Start a new patent draft to begin.");
      if (logoRefused !== null) {
        toast.error(
          `The client was added without its logo. ${refusalDetail(logoRefused, "The picture could not be stored.")}`,
        );
      }
      onAdded(client);
    } catch (error) {
      toast.error(
        refusalDetail(
          error,
          "The client could not be added. Please try again.",
        ),
      );
    }
  }
  return { add, pending: create.isPending };
}

/**
 * Adds a client: the organisation and its industry, an optional logo, and an
 * optional section for the person the attorney deals with there. The reference
 * is the server's to give.
 */
export function AddClientDialog({
  open,
  onClose,
  onAdded,
}: {
  open: boolean;
  onClose: () => void;
  /** Told the client once it exists, after the dialog has closed: the palette carries on into its first draft. */
  onAdded?: (client: ClientResponse) => void;
}) {
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [logo, setLogo] = useState<File | null>(null);
  const preview = usePreview(logo);
  const sentenceId = useId();
  const set = (key: "name" | "industry") => (value: string) => {
    setDraft((was) => ({ ...was, [key]: value }));
  };

  function close() {
    setDraft(EMPTY);
    setLogo(null);
    onClose();
  }
  const { add, pending } = useAddClient((client) => {
    close();
    onAdded?.(client);
  });

  return (
    <Dialog open={open} onOpenChange={(next) => !next && close()}>
      <DialogContent aria-describedby={sentenceId}>
        <DialogTitle>Add a new client</DialogTitle>
        <DialogDescription id={sentenceId}>
          These details set up the client record. Patent drafts are then created
          under this client.
        </DialogDescription>
        <form
          className="grid gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            void add(draft, logo);
          }}
        >
          <TextField
            label="Inventor/Organisation name"
            placeholder="e.g. Northwind Coatings"
            value={draft.name}
            onChange={set("name")}
          />
          <TextField
            label="Industry type"
            placeholder="e.g. Materials & coatings"
            value={draft.industry}
            onChange={set("industry")}
          />
          <LogoField
            name={draft.name}
            shownUrl={preview}
            onChoose={setLogo}
            onRemove={() => {
              setLogo(null);
            }}
          />
          <KeyContactSection
            value={draft.contact}
            onChange={(contact) => {
              setDraft((was) => ({ ...was, contact }));
            }}
          />
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={close}>
              Cancel
            </Button>
            <Button type="submit" disabled={!isComplete(draft) || pending}>
              {pending ? "Adding…" : "Add client"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
